/**
 * Exercise Document AST Node
 *
 * The root node representing a complete exercise:
 * Exercise = Question + Lines + Tools + Validation
 */

import { z } from 'zod';
import { TextBlockSchema, LineBlock } from './blocks';
import { LineBlockSchema } from './blocks';
import { NumberLineBlock, NumberLineBlockSchema } from './number-line';
import { TableBlock, TableBlockSchema } from './table';
import { RadioOptionsBlockSchema } from './radio-options';
import { MatcherBlock, MatcherBlockSchema } from './matcher';
import { PairMatcherBlock, PairMatcherBlockSchema } from './pair-matcher';
import { VerticalOperationBlockSchema } from './vertical-operation';
import { ToolNodeSchema } from './tools';
import { ValidationNodeSchema } from './validation';
import { AST_TYPES } from '../constants';
import type { InlineNode } from './inline';

// ═══════════════════════════════════════════════════════════════════════════
// Content Block (LineBlock | TableBlock | RadioOptionsBlock)
// ═══════════════════════════════════════════════════════════════════════════

export const ContentBlockSchema = z.union([LineBlockSchema, TableBlockSchema, RadioOptionsBlockSchema, NumberLineBlockSchema, MatcherBlockSchema, PairMatcherBlockSchema, VerticalOperationBlockSchema]);
export type ContentBlock = z.infer<typeof ContentBlockSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// Exercise Document
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Exercise document schema - the complete exercise structure
 *
 * Structure:
 * 1. Question: What the student should do
 * 2. Lines: Visual rows with inline elements
 * 3. Tools: Standalone interactive elements (optional)
 * 4. Validation: How to check answers
 *
 * @example
 * {
 *   type: "ExerciseDocument",
 *   question: {
 *     type: "TextBlock",
 *     value: "أقارن كل عددين باستعمال الرمر المناسب"
 *   },
 *   lines: [
 *     {
 *       type: "LineBlock",
 *       inlines: [
 *         { type: "HighlightedText", value: "151 000", highlight: [0, 3] },
 *         { type: "InlineInput", name: "x", length: 1 },
 *         { type: "HighlightedText", value: "54 000", highlight: [0, 3] }
 *       ]
 *     }
 *   ],
 *   tools: [
 *     { type: "DragOptions", options: ["<", ">", "="] }
 *   ],
 *   validation: {
 *     type: "SimpleValidation"
 *   }
 * }
 */
export const ExerciseDocumentSchema = z.object({
  type: z.literal(AST_TYPES.EXERCISE_DOCUMENT),
  question: TextBlockSchema.optional(),
  lines: z.array(ContentBlockSchema),
  tools: z.array(ToolNodeSchema).optional(),
  validation: ValidationNodeSchema.optional()
}).superRefine((doc, ctx) => {
  const seen = new Set<string>();
  const record = (name: string, path: (string | number)[]): void => {
    if (seen.has(name)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Duplicate name "${name}" — widget/image names must be unique across the document`,
        path,
      });
    } else {
      seen.add(name);
    }
  };

  const visitInlines = (inlines: InlineNode[], basePath: (string | number)[]): void => {
    inlines.forEach((inline, i) => {
      if (inline.type === AST_TYPES.INLINE_INPUT || inline.type === AST_TYPES.FRACTION || inline.type === AST_TYPES.IMAGE) {
        record(inline.name, [...basePath, i, 'name']);
      }
    });
  };

  doc.lines.forEach((block, bi) => {
    if (block.type === AST_TYPES.LINE_BLOCK) {
      visitInlines((block as LineBlock).inlines, ['lines', bi, 'inlines']);
    } else if (block.type === AST_TYPES.TABLE_BLOCK) {
      (block as TableBlock).rows.forEach((row, ri) => {
        row.cells.forEach((cell, ci) => {
          visitInlines(cell.inlines, ['lines', bi, 'rows', ri, 'cells', ci, 'inlines']);
        });
      });
    } else if (block.type === AST_TYPES.NUMBER_LINE_BLOCK) {
      (block as NumberLineBlock).positions.forEach((pos, pi) => {
        visitInlines(pos.inlines, ['lines', bi, 'positions', pi, 'inlines']);
      });
    } else if (block.type === AST_TYPES.MATCHER_BLOCK) {
      const m = block as MatcherBlock;
      record(m.name, ['lines', bi, 'name']);
      m.rows.forEach((row, ri) => {
        row.items.forEach((item, ii) => {
          visitInlines(item.inlines as InlineNode[], ['lines', bi, 'rows', ri, 'items', ii, 'inlines']);
        });
      });
    } else if (block.type === AST_TYPES.PAIR_MATCHER_BLOCK) {
      const pm = block as PairMatcherBlock;
      record(pm.name, ['lines', bi, 'name']);
      [...pm.topItems, ...pm.bottomItems].forEach((item, ii) => {
        visitInlines(item.inlines as InlineNode[], ['lines', bi, 'items', ii, 'inlines']);
      });
    } else if (block.type === AST_TYPES.RADIO_OPTIONS) {
      record(block.name, ['lines', bi, 'name']);
    }
  });
});

export type ExerciseDocument = z.infer<typeof ExerciseDocumentSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// Factory Function
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Create an exercise document
 */
export function createExerciseDocument(
  question: z.infer<typeof TextBlockSchema> | undefined,
  lines: ContentBlock[],
  validation?: z.infer<typeof ValidationNodeSchema>,
  tools?: z.infer<typeof ToolNodeSchema>[]
): ExerciseDocument {
  return ExerciseDocumentSchema.parse({
    type: AST_TYPES.EXERCISE_DOCUMENT,
    question,
    lines,
    tools,
    validation
  });
}

// ═══════════════════════════════════════════════════════════════════════════
// Type Guard
// ═══════════════════════════════════════════════════════════════════════════

export function isExerciseDocument(value: unknown): value is ExerciseDocument {
  return ExerciseDocumentSchema.safeParse(value).success;
}
